import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../contexts/AuthContext";
import ProviderSelectionModal from "./ProviderSelectionModal";
import MobileUpdateModal from "./MobileUpdateModal";
import EmailUpdateModal from "./EmailUpdateModal";
import LoaTransactionHistoryModal from "./LoaTransactionHistoryModal";
import GenerateMobileAppCredentialsModal from "./GenerateMobileAppCredentialsModal";
import MyPlanAndBenefitsMain from "./MyPlanAndBenefitsMain";
import InitialFeedbackModal from "./InitialFeedbackModal";
import officialLogo from "../assets/official_logo.png";
import CREDS from "../assets/CREDS.png";
import EMAIL from "../assets/EMAIL.png";
import FEEDBACK from "../assets/FEEDBACK.png";
import LOA from "../assets/LOA.png";
import MOBILE from "../assets/MOBILE.png";
import PLAN from "../assets/PLAN.png";
import TRANSACTION from "../assets/TRANSACTION.png";
import "./serviceUi.css";
import "./DynamicContentLoader.css";
import feedbackService from "../services/feedbackService";

// One tile per service. The tone is the colour members already know the service by.
const SERVICES = [
  { action: "generate-approval", tone: "cyan", icon: LOA, label: "Generate LOA", hint: "Letter of Authorization", aria: "Generate LOA" },
  { action: "plan-and-benefits", tone: "purple", icon: PLAN, label: "My Plan & Coverage", hint: "Plan details and limits", aria: "View Plan and Coverage" },
  { action: "generate-mobile-app-creds", tone: "yellow", icon: CREDS, label: "App Credentials", hint: "Mobile app login details", aria: "Generate Mobile App Credentials" },
  { action: "loa-transaction-history", tone: "lime", icon: TRANSACTION, label: "Transaction History", hint: "Your past LOAs", aria: "View Transaction History" },
  { action: "mobile-update", tone: "red", icon: MOBILE, label: "Update Mobile", hint: "New mobile number", aria: "Update Mobile" },
  { action: "email-update", tone: "indigo", icon: EMAIL, label: "Update Email", hint: "New email address", aria: "Update Email" },
];

const DynamicContentLoader = () => {
  const {
    isLoading,
    setIsLoading,
    action,
    isModalOpen,
    validateEncryptedData,
    setIsModalOpen,
  } = useAuth();
  const location = useLocation();
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [encryptedData, setEncryptedData] = useState(null);
  const [isInitialFeedbackModalOpen, setIsInitialFeedbackModalOpen] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const actionDo = queryParams.get("action");
    const encryptedData = queryParams.get("data");
    setEncryptedData(encryptedData);

    if (actionDo && encryptedData) {
      validateEncryptedData(encryptedData, actionDo);
      setIsUnauthorized(false);
    } else {
      // Opened without a Messenger link: nothing to verify, show the notice
      setIsUnauthorized(true);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleOpenFeedbackModal = async () => {
    setIsModalOpen(false);
    const timer = setTimeout(async () => {
      try {
        const hasSubmitted = await feedbackService.hasSubmittedFeedback(encryptedData);
        if (!hasSubmitted) {
          setIsInitialFeedbackModalOpen(true);
        }
      } catch (error) {
        console.error("Error checking feedback submission:", error);
      }
    }, 900);

    return () => clearTimeout(timer);
  };

  const openModal = (actionType) => {
    setIsModalOpen(true);
    setIsUnauthorized(false);
    window.history.pushState(
      {},
      "",
      `?action=${actionType}&data=${encryptedData}`
    );
    validateEncryptedData(encryptedData, actionType);
  };

  return (
    <div className="menu-page flex flex-col items-center min-h-screen px-4 py-5 sm:py-8">
      {isLoading && (
        <div className="loader-container" role="status" aria-live="polite">
          <div className="spinner"></div>
          <span className="loader-text">Loading...</span>
        </div>
      )}

      <ProviderSelectionModal
        isOpen={isModalOpen && action === "generate-approval"}
        onClose={handleOpenFeedbackModal}
      />
      <MobileUpdateModal
        isOpen={isModalOpen && action === "mobile-update"}
        onClose={() => setIsModalOpen(false)}
      />
      <EmailUpdateModal
        isOpen={isModalOpen && action === "email-update"}
        onClose={() => setIsModalOpen(false)}
      />
      <GenerateMobileAppCredentialsModal
        isOpen={isModalOpen && action === "generate-mobile-app-creds"}
        onClose={() => setIsModalOpen(false)}
      />
      <LoaTransactionHistoryModal
        isOpen={isModalOpen && action === "loa-transaction-history"}
        onClose={handleOpenFeedbackModal}
      />
      <MyPlanAndBenefitsMain
        isOpen={isModalOpen && action === "plan-and-benefits"}
        onClose={handleOpenFeedbackModal}
      />
      <InitialFeedbackModal
        isOpen={isModalOpen && action === "feedback"}
        onClose={() => setIsModalOpen(false)}
      />

      {!isLoading && !isModalOpen && (
        <main className="menu-shell">
          <header className="menu-header">
            <img
              className="menu-logo"
              alt="AMAPHIL - Advanced Medical Access Philippines, Inc."
              src={officialLogo}
            />
          </header>

          {isUnauthorized ? (
            <section className="menu-notice" role="alert">
              <span className="menu-notice__icon" aria-hidden="true">
                <FontAwesomeIcon icon={faLock} />
              </span>
              <h1>Unauthorized access</h1>
              <p>
                This page only opens from a link inside Facebook Messenger.
                Please go back to the AMAPHIL Messenger chat and try again.
              </p>
            </section>
          ) : (
            <>
              <section className="menu-intro">
                <p className="menu-eyebrow">Member services</p>
                <h1 className="menu-title">How can we help you today?</h1>
                <p className="menu-subtitle">Choose a service to get started.</p>
              </section>

              <div className="menu-grid">
                {SERVICES.map((service) => (
                  <button
                    key={service.action}
                    type="button"
                    data-action={service.action}
                    onClick={() => openModal(service.action)}
                    className={`menu-tile menu-tile--${service.tone}`}
                    aria-label={service.aria}
                  >
                    <span className="menu-tile__icon">
                      <img alt="" src={service.icon} />
                    </span>
                    <span className="menu-tile__label">{service.label}</span>
                    <span className="menu-tile__hint">{service.hint}</span>
                  </button>
                ))}
              </div>

              <button
                type="button"
                data-action="feedback"
                onClick={() => openModal("feedback")}
                className="feedback-pill"
                aria-label="Give Feedback"
              >
                <span className="feedback-pill__icon">
                  <img alt="" src={FEEDBACK} />
                </span>
                Give feedback
              </button>
              <p className="menu-footnote">
                Need something else? Close this window and type <b>menu</b> in the chat.
              </p>
            </>
          )}
        </main>
      )}

      <InitialFeedbackModal
        isOpen={isInitialFeedbackModalOpen}
        onClose={() => setIsInitialFeedbackModalOpen(false)}
      />
    </div>
  );
};

export default DynamicContentLoader;
